<template>
  <div class="alarm-record-table" :aria-busy="loading">
    <div class="table-scroll">
      <table>
        <thead>
          <tr>
            <th v-if="prioritizeAlarmColumns">告警编码</th>
            <th v-if="prioritizeAlarmColumns">告警描述</th>
            <th v-if="showStation">电站名称</th>
            <th>设备名称</th>
            <th>设备UUID</th>
            <th>设备类型</th>
            <th v-if="!prioritizeAlarmColumns">告警编码</th>
            <th>故障编码</th>
            <th>告警分类</th>
            <th v-if="!prioritizeAlarmColumns">告警描述</th>
            <th>告警状态</th>
            <th>告警开始时间</th>
            <th>恢复时间</th>
            <th>触发次数</th>
            <th>恢复次数</th>
            <th>值</th>
            <th>备注</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="alarm in pagedRecords" :key="alarm.rowId">
            <template v-if="prioritizeAlarmColumns">
              <td class="mono alarm-code-cell">
                <button v-if="isAlarmCodeLinkable(alarm.alarmCode)" type="button" class="alarm-code-link"
                  title="定位到诊断内容" @click="$emit('alarm-code-click', alarm.alarmCode)">
                  {{ alarm.alarmCode }}
                </button>
                <span v-else>{{ alarm.alarmCode || '-' }}</span>
              </td>
              <td>{{ alarm.content || '-' }}</td>
            </template>
            <td v-if="showStation">{{ alarm.stationName || '-' }}</td>
            <td>{{ alarm.equipmentName || '-' }}</td>
            <td>{{ alarm.equipmentUuid || alarm.equipmentId || '-' }}</td>
            <td>{{ alarm.equipmentType || '-' }}</td>
            <td v-if="!prioritizeAlarmColumns" class="mono alarm-code-cell">
              <button v-if="isAlarmCodeLinkable(alarm.alarmCode)" type="button" class="alarm-code-link" title="定位到诊断内容"
                @click="$emit('alarm-code-click', alarm.alarmCode)">
                {{ alarm.alarmCode }}
              </button>
              <span v-else>{{ alarm.alarmCode || '-' }}</span>
            </td>
            <td>{{ alarm.faultCode || '-' }}</td>
            <td>
              <span :class="getCategoryClass(alarm.category)" class="category-badge">
                {{ getCategoryLabel(alarm.category) }}
              </span>
            </td>
            <td v-if="!prioritizeAlarmColumns">{{ alarm.content || '-' }}</td>
            <td>
              <span :class="getStateClass(alarm.state)" class="state-badge">{{ alarm.state || '-' }}</span>
            </td>
            <td>{{ alarm.firstTriggerTime || '-' }}</td>
            <td>{{ alarm.recoveryTime || alarm.endTime || '-' }}</td>
            <td>{{ alarm.triggerCount ?? '-' }}</td>
            <td>{{ alarm.recoverCount ?? '-' }}</td>
            <td>
              <el-tooltip
                :content="displayValue(alarm.value)"
                placement="top"
                :disabled="!isValueOverflow(alarm.value)"
                popper-class="alarm-value-tooltip"
              >
                <span class="value-cell" :class="{ 'has-ellipsis': isValueOverflow(alarm.value) }">
                  {{ displayValue(alarm.value) }}
                </span>
              </el-tooltip>
            </td>
            <td class="remark-cell">
              <el-tooltip
                :content="displayValue(alarm.remark || alarm.suggestion || '-')"
                placement="top"
                :disabled="!isValueOverflow(alarm.remark || alarm.suggestion)"
                popper-class="alarm-value-tooltip"
              >
                <span class="cell-overflow">{{ alarm.remark || alarm.suggestion || '-' }}</span>
              </el-tooltip>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="!loading && !records.length" class="empty-table">暂无告警数据</div>
    <div v-if="pagination && totalCount" class="record-pagination">
      <span>共 {{ totalCount }} 条</span>
      <v-pagination v-model="page" :length="pageCount" :total-visible="5" density="compact" size="small" />
    </div>
    <div v-if="loading" class="table-loading" role="status">
      <v-progress-circular indeterminate color="primary" size="26" width="2" />
      <span>正在查询...</span>
    </div>
  </div>
</template>

<script setup>
import { computed, watch } from 'vue'
import { getCategoryClass, getCategoryLabel } from './category'

const props = defineProps({
  records: { type: Array, default: () => [] },
  showStation: { type: Boolean, default: true },
  pagination: { type: Boolean, default: false },
  pageSize: { type: Number, default: 10 },
  // 服务端分页：records 已是当前页数据，总数由 serverTotal 提供，翻页交给外部
  serverPagination: { type: Boolean, default: false },
  serverTotal: { type: Number, default: 0 },
  loading: { type: Boolean, default: false },
  alarmCodeLinkable: { type: Function, default: null },
  prioritizeAlarmColumns: { type: Boolean, default: false },
})
defineEmits(['alarm-code-click'])

const page = defineModel('page', { type: Number, default: 1 })
const totalCount = computed(() => props.serverPagination ? props.serverTotal : props.records.length)
const pageCount = computed(() => Math.max(1, Math.ceil(totalCount.value / props.pageSize)))
const pagedRecords = computed(() => {
  if (!props.pagination) return props.records
  // 服务端分页：records 即当前页，直接展示
  if (props.serverPagination) return props.records
  const start = (page.value - 1) * props.pageSize
  return props.records.slice(start, start + props.pageSize)
})

watch(() => props.records, () => {
  // 前端分页时数据变化回到第 1 页；服务端分页由外部控制页码，不重置
  if (!props.serverPagination) page.value = 1
}, { deep: false })

watch(pageCount, (count) => {
  if (page.value > count) page.value = count
})

function getStateClass(state) {
  if (state === '未恢复') return 'state-open'
  if (state === '已恢复' || state === '已自动恢复') return 'state-done'
  return ''
}

// 值/备注适配：空值显示占位，避免『-』等短文本也触发宽度撑开
function displayValue(val) {
  return val !== '' && val != null ? String(val) : '-'
}

// 长度阈值：短值（如 --/20kW）不截断，长值截断为 160px 固定宽并允许鼠标悬停查看完整值
const VALUE_ELLIPSIS_THRESHOLD = 18

function isValueOverflow(val) {
  const text = displayValue(val)
  return text.length > VALUE_ELLIPSIS_THRESHOLD
}

function isAlarmCodeLinkable(alarmCode) {
  return Boolean(alarmCode && props.alarmCodeLinkable?.(alarmCode))
}
</script>

<style scoped lang="scss">
@import './category.scss';

.alarm-record-table {
  position: relative;
  width: 100%;
  min-height: 160px;
  overflow: hidden;
  background: transparent;
}

.table-loading {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: rgba(46, 46, 46, 0.6);
  font-size: 13px;
  background: rgba(255, 255, 255, 0.82);
}

.table-scroll {
  overflow: auto;
}

table {
  width: 100%;
  min-width: 1280px;
  border-collapse: collapse;
  background: transparent;
}

th,
td {
  padding: 10px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  text-align: left;
  white-space: nowrap;
  font-size: 13px;
  vertical-align: middle;
}

th {
  color: rgba(46, 46, 46, 0.85);
  font-weight: 500;
  background: rgba(0, 0, 0, 0.02);
}

td {
  color: #344054;
}

tbody tr:hover td {
  background: rgba(230, 243, 255, 0.2);
}

tbody tr:last-child td {
  border-bottom: none;
}

.mono {
  font-family: inherit;
  color: #30416f;
}

// .alarm-code-cell {
//   font-weight: 600;
// }

.alarm-code-link {
  padding: 0;
  border: 0;
  background: transparent;
  color: #3a68ff;
  font: inherit;
  letter-spacing: 0;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  &:focus-visible {
    border-radius: 2px;
    outline: 2px solid rgba(58, 104, 255, 0.35);
    outline-offset: 2px;
  }
}

.category-badge,
.state-badge {
  display: inline-flex;
  align-items: center;
  height: 22px;
  white-space: nowrap;
}

.category-badge {
  justify-content: flex-start;
  width: auto;
  font-weight: 500;
}

.state-open {
  color: #ff4d4f;
  font-weight: 600;
}

.state-done {
  color: #2e7d32;
  font-weight: 600;
}

.remark-cell {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 值/备注长文本：固定列宽 + 省略号 + 悬停查看完整值 */
.value-cell,
.cell-overflow {
  display: inline-block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}

.value-cell {
  font-variant-numeric: tabular-nums;
}

.value-cell.has-ellipsis,
.cell-overflow {
  display: block;
  max-width: 220px;
}

/* 长值悬停：不限制宽度，完整显示，最多两行 */
:global(.alarm-value-tooltip) {
  max-width: 480px;
  line-height: 1.5;
  word-break: break-all;
}

.empty-table {
  padding: 28px 16px;
  color: #98a2b3;
  font-size: 13px;
  text-align: center;
}

.record-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  min-width: max-content;
  padding: 10px 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  color: #667085;
  font-size: 12px;
}
</style>
